/**
 * @fileoverview Country selector for the department details slide-in, backed
 *   by the shared lookup system (`listLookups({ lookupType: 'country' })`).
 *   Replaces free-text country editing so `jurisdiction.country` keeps the
 *   LookupRef shape ({ lookupId, lookupType, code, name }) that the onboarding
 *   wizard writes and that state lookups cascade from.
 * @module system-web/components/JurisdictionCountryField
 *
 * Responsibilities:
 * - Render Country as display text with click-to-edit behavior (matching
 *   `InlineEditableField` / `JurisdictionStateCountyField`).
 * - On edit: render a country dropdown from the lookup catalog.
 * - On change: store a LookupRef on `jurisdiction.country`, clear the dependent
 *   `state` / `county` / `fipsCode`, and persist the full jurisdiction via
 *   `onSave` (the parent derives `address.country/state/county` from it).
 *
 * Dependencies:
 * - ../hooks/useJurisdictionLookups (country lookup loading).
 * - ../utils/jurisdictionFips (toLookupRef, LookupRef).
 *
 * Architecture:
 * - Local-first. Reads lookup values from Dexie via useJurisdictionLookups.
 * - No direct API calls; persistence is delegated to the parent's `onSave`.
 *
 * Notes:
 * - Legacy tenants may store `jurisdiction.country` as a plain string; it is
 *   displayed as-is and replaced with a LookupRef on the first edit.
 *
 * @component JurisdictionCountryField
 *
 * Purpose:
 * - Lookup-backed Country editor for the department details slide-in.
 *
 * Props:
 * - jurisdiction: The tenant's current jurisdiction object.
 * - onSave: Callback receiving the full next jurisdiction object.
 * - showLabel: Render the field label (default true).
 * - disabled: Disable editing.
 *
 * Behavior:
 * - Display mode: country name shown with dashed underline + pencil.
 * - Click to enter edit mode → dropdown; selecting saves immediately and exits.
 * - Click-away exits edit mode without saving.
 *
 * Data Source:
 * - local (Dexie via useJurisdictionLookups hook).
 *
 * Constraints:
 * - No direct API calls; all mutations via the parent onSave callback.
 */
import { useCallback, useState, useRef, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { useJurisdictionLookups } from '../hooks/useJurisdictionLookups';
import { toLookupRef, type LookupRef } from '../utils/jurisdictionFips';

export interface JurisdictionCountryFieldProps {
  jurisdiction: Record<string, unknown>;
  onSave: (nextJurisdiction: Record<string, unknown>) => void;
  showLabel?: boolean;
  disabled?: boolean;
}

const SELECT_CLS =
  'w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors disabled:bg-gray-50 disabled:text-gray-400';
const LABEL_CLS = 'text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5';

function isSameCountry(prev: unknown, next: LookupRef): boolean {
  const norm = (v: unknown) => (typeof v === 'string' ? v.trim().toLowerCase() : '');
  if (typeof prev === 'string') {
    const legacy = norm(prev);
    return legacy !== '' && (legacy === norm(next.code) || legacy === norm(next.name));
  }
  const ref = prev as LookupRef | undefined;
  return !!ref?.code && norm(ref.code) === norm(next.code);
}

/**
 * @process applyCountryChange
 * Purpose: Build the next jurisdiction after a country selection. State,
 *          county and FIPS cascade from country, so they are cleared whenever
 *          the country actually changes; re-selecting the same country is a
 *          no-op for the dependent fields. A legacy string country matches
 *          the lookup by either code or display name (case-insensitive).
 * Idempotency: REQUIRED — pure.
 */
export function applyCountryChange(
  jurisdiction: Record<string, unknown>,
  nextCountry: LookupRef | undefined,
): Record<string, unknown> {
  if (nextCountry && isSameCountry(jurisdiction.country, nextCountry)) {
    return { ...jurisdiction, country: nextCountry };
  }
  return { ...jurisdiction, country: nextCountry, state: undefined, county: undefined, fipsCode: undefined };
}

export function countryDisplayName(country: unknown): string {
  if (typeof country === 'string') return country;
  const ref = country as LookupRef | undefined;
  return ref?.name || ref?.code || '';
}

export default function JurisdictionCountryField({
  jurisdiction,
  onSave,
  showLabel = true,
  disabled = false,
}: JurisdictionCountryFieldProps) {
  const rawCountry = jurisdiction.country;
  const countryRef = rawCountry && typeof rawCountry === 'object' ? (rawCountry as LookupRef) : undefined;
  const { countries, loadingCountries } = useJurisdictionLookups();

  const [editing, setEditing] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback(
    (code: string) => {
      const item = countries.find((c) => c.code === code);
      onSave(applyCountryChange(jurisdiction, toLookupRef(item as Record<string, any> | undefined)));
      setEditing(false);
    },
    [countries, jurisdiction, onSave],
  );

  useEffect(() => {
    if (!editing) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setEditing(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editing]);

  const enterEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setEditing(true);
  }, [disabled]);

  const display = countryDisplayName(rawCountry);

  return (
    <div>
      {showLabel && <div className={LABEL_CLS}>Country</div>}
      {editing ? (
        <div ref={wrapperRef} onClick={(e) => e.stopPropagation()}>
          <select
            value={countryRef?.code ?? ''}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled || loadingCountries}
            className={SELECT_CLS}
            aria-label="Country"
            title="Country"
            autoFocus
          >
            <option value="">{loadingCountries ? 'Loading…' : 'Select country'}</option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div
          ref={wrapperRef}
          onClick={enterEdit}
          className="group/jcf relative inline-flex items-center gap-1 w-full cursor-text"
          title="Click to edit"
        >
          <span className="text-sm text-gray-800 border-b border-dashed border-gray-300 group-hover/jcf:border-solid group-hover/jcf:border-orange-400 transition-colors pb-px">
            {display || <span className="text-gray-400">—</span>}
          </span>
          <Pencil className="h-3 w-3 text-gray-300 opacity-25 group-hover/jcf:opacity-70 group-hover/jcf:text-orange-500 transition-all shrink-0" />
        </div>
      )}
    </div>
  );
}
